import { isAndroidApp } from './platform';

// Stripe Payment Links — the only place these URLs live. The backend maps the
// matching Price IDs back to a plan in STRIPE_PRICE_TO_PLAN (server.py).
//
// The webhook matches a payment to a Mimique account by client_reference_id
// first and the checkout email only as a fallback, so always pass the user id
// when we have one: it survives the customer editing the address on Stripe's
// page, or paying through Link or PayPal under a different one. The email is
// still prefilled because it is the fallback and it saves them typing.
export const PAYMENT_LINKS = {
    pro: 'https://buy.stripe.com/aFa4gB9YBgGceKTcn8d3i00',
    unlimited: 'https://buy.stripe.com/aFacN76Mp89GdGP5YKd3i01',
};

export const PLAN_LABELS = {
    free: 'Free',
    pro: 'Pro',
    unlimited: 'Unlimited',
};

// Headline monthly prices. One copy, so the pricing table on the landing page
// and the upgrade banners in the app can never quietly drift apart. Change a
// price here and in Stripe, nowhere else.
export const PLAN_PRICES = {
    free: '€0',
    pro: '€5.99',
    unlimited: '€8.99',
};

// What each plan actually gives you, in numbers. Keep in step with
// PLAN_PHRASE_TOTAL, PLAN_AI_MONTHLY and the free scoring cap in server.py:
// the server enforces these, this copy only describes them.
export const PLAN_LIMITS = {
    free: { phrases: 30, aiPhrases: 0, scoredRecordings: '30 in total' },
    pro: { phrases: 500, aiPhrases: 50, scoredRecordings: 'every recording' },
    unlimited: { phrases: 10000, aiPhrases: 2000, scoredRecordings: 'every recording' },
};

// Deep link to the pricing table on the landing page. Logged-in visitors are
// normally bounced off "/" back to the dashboard, so App.js makes an exception
// for this exact hash — keep the two in step.
export const PRICING_URL = '/#pricing';

/** Whether checkout may be offered at all in this shell.
 *
 * Google Play's Payments policy forbids a Play-distributed app from leading
 * users to any payment method other than Play's own billing, and that covers
 * linking out to Stripe. Mimique's Android app therefore sells nothing: people
 * subscribe on the web, and the app honours whatever plan the account already
 * has. It also rules out pointing at the pricing page from inside the app,
 * since that is a route to the same checkout.
 *
 * Every checkout call site is gated on this, so there is one rule, in one
 * place, to revisit if Play Billing is ever added.
 */
export const canCheckout = () => !isAndroidApp();

/** Checkout URL for a plan, tagged with the account id and email when known. */
export const checkoutUrl = (plan, email, userId) => {
    const base = PAYMENT_LINKS[plan];
    if (!base) return null;
    const params = new URLSearchParams();
    if (email) params.set('prefilled_email', email);
    // Stripe passes this straight back on the webhook event. It is what binds
    // the payment to the account, so it matters more than the email.
    if (userId) params.set('client_reference_id', userId);
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
};

/** Send the browser to Stripe checkout for the given plan. */
export const goToCheckout = (plan, email, userId) => {
    const url = checkoutUrl(plan, email, userId);
    if (url) window.location.href = url;
};
